// Shipping computation shared by cart + checkout. Zone rates (matched by
// customer province) override the flat fee; free shipping applies once the
// (post-discount) amount reaches the threshold. Admin-editable via
// settings.commerce (shipping_flat, free_shipping_threshold, shipping_zones).
export const shippingConfig = (settings) => {
  const c = settings?.commerce || {};
  return {
    flat: Number(c.shipping_flat ?? 5.9),
    threshold: Number(c.free_shipping_threshold ?? 100),
    label: c.shipping_label || "Spedizione",
    zones: Array.isArray(c.shipping_zones) ? c.shipping_zones : [],
  };
};

const matchZone = (province, cfg, postalCode = "") => {
  const p = (province || "").trim().toUpperCase();
  if (!p) return null;
  const cap = (postalCode || "").trim();
  const inProvince = cfg.zones.filter((z) =>
    String(z.provinces || "")
      .split(",")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean)
      .includes(p)
  );
  // A zone with 'caps' set only applies when the customer's postal code is
  // in that list (e.g. free shipping restricted to a specific comune, not
  // the whole province). A zone with no 'caps' applies to the whole province.
  for (const z of inProvince) {
    const caps = String(z.caps || "").split(",").map((c) => c.trim()).filter(Boolean);
    if (caps.length > 0 && cap && caps.includes(cap)) return z;
  }
  return inProvince.find((z) => !String(z.caps || "").trim()) || null;
};

export const zoneCapsForProvince = (province, settings) => {
  const cfg = shippingConfig(settings);
  const p = (province || "").trim().toUpperCase();
  if (!p) return [];
  const zone = cfg.zones.find((z) =>
    String(z.provinces || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean).includes(p)
  );
  if (!zone) return [];
  return String(zone.caps || "").split(",").map((c) => c.trim()).filter(Boolean);
};

const rateForProvince = (province, cfg, postalCode = "") => {
  const zone = matchZone(province, cfg, postalCode);
  if (zone) {
    const rate = Number(zone.rate);
    if (Number.isFinite(rate)) return rate;
  }
  return cfg.flat;
};

export const computeShipping = (amountAfterDiscount, settings, freeByDiscount = false, province = "", postalCode = "") => {
  const cfg = shippingConfig(settings);
  if (freeByDiscount) return 0;
  if (cfg.threshold > 0 && amountAfterDiscount >= cfg.threshold) return 0;
  return Math.round(rateForProvince(province, cfg, postalCode) * 100) / 100;
};

export const zoneNameForProvince = (province, settings, postalCode = "") => {
  const cfg = shippingConfig(settings);
  const zone = matchZone(province, cfg, postalCode);
  return zone?.name || "";
};

export const amountToFreeShipping = (amountAfterDiscount, settings) => {
  const { threshold } = shippingConfig(settings);
  if (threshold <= 0) return 0;
  return Math.max(0, Math.round((threshold - amountAfterDiscount) * 100) / 100);
};

export const parseProvinceList = (raw) =>
  String(raw || "")
    .split(",")
    .map((p) => p.trim().toUpperCase())
    .filter(Boolean);

// Provinces where "Pagamento alla consegna" (cash on delivery) is offered — fully
// admin-editable (settings.commerce.cod_provinces), no hardcoded list.
export const codProvinces = (settings) => parseProvinceList(settings?.commerce?.cod_provinces);

// Minimum order total required to offer COD (0 = no minimum).
export const codMinAmount = (settings) => Number(settings?.commerce?.cod_min_amount ?? 0);

// Provinces with free shipping — derived from shipping_zones entries with rate 0
// (same zones already editable in Admin → Tema), no separate setting.
export const freeShippingProvinces = (settings) => {
  const cfg = shippingConfig(settings);
  const set = new Set();
  cfg.zones.forEach((z) => {
    if (Number(z.rate) === 0) parseProvinceList(z.provinces).forEach((p) => set.add(p));
  });
  return Array.from(set);
};

export const formatProvinceList = (list, sep = " · ") =>
  Array.isArray(list) && list.length ? [...list].sort().join(sep) : "";

// Above this order total, COD requires an upfront deposit (paid via Stripe) before
// the order is created — the rest stays cash-on-delivery. Not admin-configurable.
export const COD_DEPOSIT_THRESHOLD = 300;
export const COD_DEPOSIT_PERCENT = 0.5;

